import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return then.toLocaleDateString();
}

export function getProfileColor(color?: string | null): string {
  return color || '#7c3aed';
}

// API base URL for resolving relative paths
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.blahblah.chat/api/v1';
const API_HOST = API_BASE_URL.replace(/\/api\/v1$/, '');

// Resolve relative audio URL to absolute
function resolveAudioUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  // If already absolute, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Relative path - prepend API host
  return `${API_HOST}${url}`;
}

// Transform snake_case API response to camelCase
export function transformUser(apiUser: any) {
  if (!apiUser) return null;
  const rawAvatarUrl = apiUser.avatar_url || apiUser.avatarUrl;
  return {
    id: apiUser.id,
    displayName: apiUser.display_name || apiUser.displayName || 'Unknown',
    avatarUrl: rawAvatarUrl, // Avatar URLs are usually already absolute (Google/OAuth)
    provider: apiUser.provider,
    profileColor: apiUser.profile_color || apiUser.profileColor,
    isAdmin: apiUser.is_admin || apiUser.isAdmin,
  };
}

export function transformCategoryInfo(apiCategory: any) {
  if (!apiCategory) return null;
  return {
    id: apiCategory.id,
    name: apiCategory.name,
    slug: apiCategory.slug,
  };
}

export function transformBlahblah(apiBlahblah: any) {
  if (!apiBlahblah) return null;
  const rawAudioUrl = apiBlahblah.audio_url || apiBlahblah.audioUrl;
  return {
    id: apiBlahblah.id,
    audioUrl: resolveAudioUrl(rawAudioUrl),
    durationMs: apiBlahblah.duration_ms || apiBlahblah.durationMs,
    createdAt: apiBlahblah.created_at || apiBlahblah.createdAt,
    likesCount: apiBlahblah.likes_count ?? apiBlahblah.likesCount ?? 0,
    viewsCount: apiBlahblah.views_count ?? apiBlahblah.viewsCount ?? 0,
    isLiked: apiBlahblah.is_liked ?? apiBlahblah.isLiked ?? false,
    isViewed: apiBlahblah.is_viewed ?? apiBlahblah.isViewed ?? false,
    isFavorited: apiBlahblah.is_favorited ?? apiBlahblah.isFavorited ?? false,
    user: transformUser(apiBlahblah.user),
    transcript: apiBlahblah.transcript,
    tags: apiBlahblah.tags || [],
    language: apiBlahblah.language,
    parentId: apiBlahblah.parent_id || apiBlahblah.parentId,
    replyCount: apiBlahblah.reply_count ?? apiBlahblah.replyCount ?? 0,
    depth: apiBlahblah.depth ?? 0,
    rootId: apiBlahblah.root_id || apiBlahblah.rootId,
    // Thread/category fields
    aiTitle: apiBlahblah.ai_title || apiBlahblah.aiTitle || null,
    categories: (apiBlahblah.categories || []).map(transformCategoryInfo).filter(Boolean),
    isFollowed: apiBlahblah.is_followed ?? apiBlahblah.isFollowed ?? null,
  };
}

export function transformPaginatedResponse<T>(
  apiResponse: any,
  transformItem: (item: any) => T
) {
  return {
    items: (apiResponse.items || []).map(transformItem),
    nextCursor: apiResponse.next_cursor || apiResponse.nextCursor,
    hasMore: apiResponse.has_more ?? apiResponse.hasMore ?? false,
    stats: apiResponse.stats ? {
      totalBlahblahs: apiResponse.stats.total_blahblahs ?? apiResponse.stats.totalBlahblahs,
      totalViews: apiResponse.stats.total_views ?? apiResponse.stats.totalViews,
      averageViews: apiResponse.stats.average_views ?? apiResponse.stats.averageViews,
    } : undefined,
  };
}

export function transformGroup(apiGroup: any) {
  if (!apiGroup) return null;
  return {
    id: apiGroup.id,
    name: apiGroup.name,
    description: apiGroup.description,
    hashtags: apiGroup.hashtags || [],
    color: apiGroup.color,
    headerImageUrl: apiGroup.header_image_url || apiGroup.headerImageUrl,
    badgeIconUrl: apiGroup.badge_icon_url || apiGroup.badgeIconUrl,
    visibility: apiGroup.visibility || 'public',
    memberCount: apiGroup.member_count ?? apiGroup.memberCount ?? 0,
    isActive: apiGroup.is_active ?? apiGroup.isActive ?? true,
    ownerId: apiGroup.owner_id || apiGroup.ownerId,
    owner: apiGroup.owner ? transformUser(apiGroup.owner) : null,
    createdAt: apiGroup.created_at || apiGroup.createdAt,
    updatedAt: apiGroup.updated_at || apiGroup.updatedAt,
    myRole: apiGroup.my_membership?.role || apiGroup.my_role || apiGroup.myRole || null,
    myStatus: apiGroup.my_membership?.status || apiGroup.my_status || apiGroup.myStatus || null,
  };
}

export function transformGroupMember(apiMember: any) {
  if (!apiMember) return null;
  return {
    id: apiMember.id,
    groupId: apiMember.group_id || apiMember.groupId,
    userId: apiMember.user_id || apiMember.userId,
    user: apiMember.user ? transformUser(apiMember.user) : null,
    role: apiMember.role,
    status: apiMember.status,
    invitedBy: apiMember.invited_by || apiMember.invitedBy,
    joinedAt: apiMember.joined_at || apiMember.joinedAt,
    createdAt: apiMember.created_at || apiMember.createdAt,
  };
}

export function transformGroupPost(apiPost: any) {
  if (!apiPost) return null;
  return {
    id: apiPost.id,
    groupId: apiPost.group_id || apiPost.groupId,
    blahblah: transformBlahblah(apiPost.blahblah || apiPost),
    postedBy: apiPost.posted_by || apiPost.postedBy,
    isPinned: apiPost.is_pinned ?? apiPost.isPinned ?? false,
    pinnedBy: apiPost.pinned_by || apiPost.pinnedBy,
    pinnedAt: apiPost.pinned_at || apiPost.pinnedAt,
    createdAt: apiPost.created_at || apiPost.createdAt,
  };
}

export function transformGroupInvite(apiInvite: any) {
  if (!apiInvite) return null;
  return {
    id: apiInvite.id,
    groupId: apiInvite.group_id || apiInvite.groupId,
    createdBy: apiInvite.created_by || apiInvite.createdBy,
    inviteCode: apiInvite.invite_code || apiInvite.inviteCode,
    inviteUrl: apiInvite.invite_url || apiInvite.inviteUrl || '',
    maxUses: apiInvite.max_uses ?? apiInvite.maxUses ?? null,
    useCount: apiInvite.use_count ?? apiInvite.useCount ?? 0,
    isActive: apiInvite.is_active ?? apiInvite.isActive ?? true,
    expiresAt: apiInvite.expires_at || apiInvite.expiresAt || null,
    createdAt: apiInvite.created_at || apiInvite.createdAt,
  };
}

export function transformInviteInfo(apiInfo: any) {
  if (!apiInfo) return null;
  // Backend sends { group: {...}, is_expired, is_full } structure
  const group = apiInfo.group;
  const isExpired = apiInfo.is_expired ?? apiInfo.isExpired ?? false;
  const isFull = apiInfo.is_full ?? apiInfo.isFull ?? false;
  return {
    groupId: group?.id || apiInfo.group_id || apiInfo.groupId,
    groupName: group?.name || apiInfo.group_name || apiInfo.groupName,
    groupDescription: group?.description || apiInfo.group_description || apiInfo.groupDescription,
    memberCount: group?.member_count ?? group?.memberCount ?? apiInfo.member_count ?? apiInfo.memberCount ?? 0,
    inviterName: apiInfo.inviter_name || apiInfo.inviterName || null,
    isValid: !isExpired && !isFull,
  };
}

// Category transforms
export function transformCategory(apiCategory: any) {
  if (!apiCategory) return null;
  return {
    id: apiCategory.id,
    name: apiCategory.name,
    slug: apiCategory.slug,
    icon: apiCategory.icon,
    description: apiCategory.description || null,
    displayOrder: apiCategory.display_order ?? apiCategory.displayOrder ?? 0,
    isAdminOnly: apiCategory.is_admin_only ?? apiCategory.isAdminOnly ?? false,
    isAdultOnly: apiCategory.is_adult_only ?? apiCategory.isAdultOnly ?? false,
    isActive: apiCategory.is_active ?? apiCategory.isActive ?? true,
    isStarred: apiCategory.is_starred ?? apiCategory.isStarred ?? false,
  };
}

export function transformThreadFollow(apiFollow: any) {
  if (!apiFollow) return null;
  return {
    id: apiFollow.id,
    userId: apiFollow.user_id || apiFollow.userId,
    blahblahId: apiFollow.blahblah_id || apiFollow.blahblahId,
    notificationsEnabled: apiFollow.notifications_enabled ?? apiFollow.notificationsEnabled ?? true,
    createdAt: apiFollow.created_at || apiFollow.createdAt,
  };
}

export function transformFollowedThread(apiThread: any) {
  if (!apiThread) return null;
  return {
    blahblah: transformBlahblah(apiThread.blahblah),
    notificationsEnabled: apiThread.notifications_enabled ?? apiThread.notificationsEnabled ?? true,
    followedAt: apiThread.followed_at || apiThread.followedAt,
    newRepliesCount: apiThread.new_replies_count ?? apiThread.newRepliesCount ?? 0,
  };
}
